import { Navigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { SurveyExecutionSkeleton } from '@/shared/components/skeletons'
import { SurveyCompletionView } from '../components/survey-execution/SurveyCompletionView'
import { SurveyExecutionFrame } from '../components/survey-execution/SurveyExecutionFrame'
import { SurveyQuestionCardContent } from '../components/survey-execution/SurveyQuestionCardContent'
import { SurveySubmitSuccessDialog } from '../components/survey-execution/SurveySubmitSuccessDialog'
import { SurveyQuestionRenderer } from '../components/survey-execution/questions/SurveyQuestionRenderer'
import { SurveySessionBlockedView } from '../components/SurveySessionBlockedView'
import { SURVEY_TARGET_REACHED_IN_SESSION } from '../lib/instrument-info-messages'
import { buildCompletionReward } from '../lib/survey-completion-reward'
import { resolveSurveyCompletionVariant } from '../lib/survey-completion-variant'
import { resolveSurveyNextButtonLabel } from '../lib/survey-execution-session'
import { surveyNextBlockedMessage } from '../lib/survey-answer'
import type { SurveyDefinition, SurveyDraftAnswer, SurveyQuestion } from '../lib/survey-question.types'
import type { ProviderInstrumentInfo, ProviderReward, ProviderSubmitConfirmation } from '../lib/types'
import { surveyEntryLoadingSx } from '../styles/surveyEntryStyles'
import { surveyQuestionCardInnerSx } from '../styles/surveyExecutionStyles'

export interface SurveyExecutionPageViewProps {
  instrumentId: string | undefined
  responseId: string | undefined
  targetBlocked: boolean
  sessionLoading: boolean
  infoLoading: boolean
  sessionError: boolean
  definition: SurveyDefinition | null
  currentQuestion: SurveyQuestion | null
  surveyTitle: string
  surveyDescription: string | null
  estimatedMinutes: number | null
  questionIndex: number
  totalQuestions: number
  showRequiredError: boolean
  draftAnswer: SurveyDraftAnswer
  nextDisabled: boolean
  nextPending: boolean
  isLastQuestion: boolean
  targetReachedLive: boolean
  showCompletionScreen: boolean
  isReturnAfterSubmit: boolean
  info: ProviderInstrumentInfo | undefined
  submitConfirmation: ProviderSubmitConfirmation | null
  completionReward: ProviderReward | null
  submitSuccessModalOpen: boolean
  onNavigateInfo: () => void
  onDraftChange: (value: SurveyDraftAnswer) => void
  onBack: () => void
  onNext: () => void
  onSubmitSuccessConfirm: () => void
  onClaimNow: () => void
  onCompletionHome: () => void
  signupSurveyMode?: boolean
  signupSurveyEkycGated?: boolean
  onStartEkyc?: () => void
  onRemindLater?: () => void
  webSurveySignupMode?: boolean
  surveyFirstJourney?: boolean
  onCreateAccount?: () => void
}

export function SurveyExecutionPageView({
  instrumentId,
  responseId,
  targetBlocked,
  sessionLoading,
  infoLoading,
  sessionError,
  definition,
  currentQuestion,
  surveyTitle,
  surveyDescription,
  estimatedMinutes,
  questionIndex,
  totalQuestions,
  showRequiredError,
  draftAnswer,
  nextDisabled,
  nextPending,
  isLastQuestion,
  targetReachedLive,
  showCompletionScreen,
  isReturnAfterSubmit,
  info,
  submitConfirmation,
  completionReward,
  submitSuccessModalOpen,
  onNavigateInfo,
  onDraftChange,
  onBack,
  onNext,
  onSubmitSuccessConfirm,
  onClaimNow,
  onCompletionHome,
  signupSurveyMode = false,
  signupSurveyEkycGated = true,
  onStartEkyc,
  onRemindLater,
  webSurveySignupMode = false,
  surveyFirstJourney = false,
  onCreateAccount,
}: Readonly<SurveyExecutionPageViewProps>) {
  if (!instrumentId || !responseId) {
    return <Navigate to={`/instrument/${instrumentId ?? ''}/info`} replace />
  }

  if (targetBlocked) {
    return <SurveySessionBlockedView />
  }

  if (sessionLoading || infoLoading) {
    return (
      <Box sx={surveyEntryLoadingSx}>
        <SurveyExecutionSkeleton />
      </Box>
    )
  }

  if ((showCompletionScreen || isReturnAfterSubmit) && info) {
    const rewardForCompletion = buildCompletionReward(info, submitConfirmation, completionReward)
    return (
      <SurveyCompletionView
        variant={resolveSurveyCompletionVariant({
          justSubmitted: showCompletionScreen,
          guestClaimJourney: webSurveySignupMode || surveyFirstJourney,
        })}
        reward={rewardForCompletion}
        instrumentRewardEnabled={info.reward.enabled}
        onClaimNow={onClaimNow}
        onHome={onCompletionHome}
        signupSurveyMode={signupSurveyMode}
        signupSurveyEkycGated={signupSurveyEkycGated}
        webSurveySignupMode={webSurveySignupMode}
        surveyFirstJourney={surveyFirstJourney}
        onStartEkyc={onStartEkyc}
        onRemindLater={onRemindLater}
        onCreateAccount={onCreateAccount}
      />
    )
  }

  if (sessionError || !definition || !currentQuestion) {
    return (
      <SurveyExecutionFrame
        name={surveyTitle}
        description={surveyDescription}
        questionIndex={1}
        totalQuestions={1}
        estimatedCompletionMinutes={estimatedMinutes}
        questionCard={
          <Box sx={surveyQuestionCardInnerSx}>
            <Alert severity="error">Unable to load survey. Please return to the survey info page.</Alert>
          </Box>
        }
        showBack
        nextDisabled
        nextPending={false}
        onBack={onNavigateInfo}
        onNext={() => undefined}
      />
    )
  }

  const nextButtonLabel = resolveSurveyNextButtonLabel(nextPending, isLastQuestion)

  return (
    <>
      <SurveyExecutionFrame
        name={surveyTitle}
        description={surveyDescription}
        questionIndex={questionIndex}
        totalQuestions={totalQuestions}
        estimatedCompletionMinutes={estimatedMinutes}
        questionCard={
          <SurveyQuestionCardContent
            question={currentQuestion}
            questionIndex={questionIndex}
            showRequiredError={showRequiredError}
            requiredErrorMessage={surveyNextBlockedMessage(currentQuestion, draftAnswer)}
          >
            <SurveyQuestionRenderer
              question={currentQuestion}
              value={draftAnswer}
              displaySeed={responseId}
              onChange={onDraftChange}
            />
          </SurveyQuestionCardContent>
        }
        showBack
        nextDisabled={nextDisabled}
        nextPending={nextPending}
        nextLabel={nextButtonLabel}
        onBack={onBack}
        onNext={onNext}
        belowNav={
          targetReachedLive ? (
            <Alert severity="info" data-testid="survey-target-reached-inline">
              {SURVEY_TARGET_REACHED_IN_SESSION}
            </Alert>
          ) : undefined
        }
        testId="survey-execution-page"
      />
      <SurveySubmitSuccessDialog open={submitSuccessModalOpen} onConfirm={onSubmitSuccessConfirm} />
    </>
  )
}
